<script lang="ts">
  import {
    AppWindowMac,
    ArrowRight,
    Boxes,
    Clock3,
    Copy,
    ExternalLink,
    Fingerprint,
    KeyRound,
    LogOut,
    ShieldCheck,
    Sparkles,
    UploadCloud,
  } from 'lucide-svelte';

  import {
    clearSession as dropSession,
    getDefaultBackendUrl,
    loadStoredSession,
    login,
    persistSession,
    register,
    saveBackendUrl,
    type AuthMode,
    type Session,
  } from '$lib/auth';

  interface FeatureItem {
    title: string;
    kicker: string;
    summary: string;
    icon: typeof ShieldCheck;
  }

  interface WorkflowItem {
    title: string;
    detail: string;
  }

  type NoticeTone = 'error' | 'info' | 'success';
  type PageView = 'home' | 'auth' | 'workspace';

  const featureItems: FeatureItem[] = [
    {
      title: '保护',
      kicker: 'Protection',
      summary: '发布前为 Java 应用增加保护层。',
      icon: ShieldCheck,
    },
    {
      title: '授权',
      kicker: 'Identity',
      summary: '通过账号和设备状态控制访问。',
      icon: Fingerprint,
    },
    {
      title: '交付',
      kicker: 'Client',
      summary: '登录后进入工作区继续后续操作。',
      icon: AppWindowMac,
    },
  ];

  const workflowItems: WorkflowItem[] = [
    {
      title: '了解方案',
      detail: '快速了解平台面向 Java 应用交付前保护。',
    },
    {
      title: '登录账户',
      detail: '登录或注册后进入受控区域。',
    },
    {
      title: '进入客户端',
      detail: '上传和客户端操作只在登录后出现。',
    },
  ];

  const initialSession = loadStoredSession();
  const backendUrl = getDefaultBackendUrl();

  function buildLaunchUrl(backendUrl: string, currentSession: Session | null): string {
    const params = new URLSearchParams({
      backend_url: backendUrl,
      entry: 'web',
    });

    if (currentSession) {
      params.set('session', JSON.stringify({
        ...currentSession,
        backend_url: backendUrl,
      }));
    }

    return `jvmsense3://launch?${params.toString()}`;
  }

  let session = $state<Session | null>(initialSession);
  let currentView = $state<PageView>(initialSession ? 'workspace' : 'home');
  let authMode = $state<AuthMode>('login');
  let username = $state('');
  let email = $state('');
  let identifier = $state('');
  let password = $state('');
  let authNotice = $state<{ text: string; tone: NoticeTone } | null>(null);
  let launchNotice = $state<{ text: string; tone: NoticeTone } | null>(null);
  let isAuthSubmitting = $state(false);

  const launchUrl = $derived(buildLaunchUrl(backendUrl, session));
  const authHeading = $derived(authMode === 'login' ? '登录工作区' : '创建工作区');
  const authSubcopy = $derived(
    authMode === 'login'
      ? '登录后进入工作区，继续使用桌面客户端。'
      : '注册后进入工作区，后续操作会出现在登录后的页面。',
  );
  const primaryAuthLabel = $derived(
    isAuthSubmitting ? '提交中…' : authMode === 'login' ? '登录工作区' : '创建工作区',
  );
  const accountSummary = $derived(
    session
      ? new Intl.DateTimeFormat('zh-CN', {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(new Date(session.expires_at * 1000))
      : '',
  );
  const sessionRemainingSeconds = $derived(
    session ? Math.max(0, session.expires_at - Math.floor(Date.now() / 1000)) : 0,
  );
  const sessionRemainingLabel = $derived(formatRemainingTime(sessionRemainingSeconds));
  const canSubmitAuth = $derived(
    password.trim().length > 0 && (
      authMode === 'login'
        ? identifier.trim().length > 0
        : username.trim().length > 0 && email.trim().length > 0
    ),
  );

  function formatRemainingTime(totalSeconds: number): string {
    if (totalSeconds <= 0) {
      return '已过期';
    }

    const days = Math.floor(totalSeconds / 86400);
    if (days > 0) {
      return `约 ${days} 天后过期`;
    }

    const hours = Math.floor(totalSeconds / 3600);
    if (hours > 0) {
      return `约 ${hours} 小时后过期`;
    }

    const minutes = Math.max(1, Math.floor(totalSeconds / 60));
    return `约 ${minutes} 分钟后过期`;
  }

  function showHome(): void {
    currentView = 'home';
  }

  function showAuth(mode: AuthMode = 'login'): void {
    authMode = mode;
    authNotice = null;
    currentView = 'auth';
  }

  function showWorkspace(): void {
    currentView = session ? 'workspace' : 'auth';
  }

  function clearSession(): void {
    session = null;
    dropSession();
    currentView = 'home';
    authNotice = {
      text: '已退出当前浏览器会话。',
      tone: 'info',
    };
  }

  function resetAuthFields(): void {
    username = '';
    email = '';
    identifier = '';
    password = '';
  }

  async function submitAuth(): Promise<void> {
    if (isAuthSubmitting) {
      return;
    }

    if (!canSubmitAuth) {
      authNotice = {
        text: '请填写完整的认证信息。',
        tone: 'error',
      };
      return;
    }

    isAuthSubmitting = true;
    authNotice = null;

    try {
      saveBackendUrl(backendUrl);

      const nextSession = authMode === 'login'
        ? await login(backendUrl, identifier, password)
        : await register(backendUrl, username, email, password);

      session = nextSession;
      persistSession(nextSession);
      resetAuthFields();
      currentView = 'workspace';
      authNotice = {
        text: authMode === 'login'
          ? `欢迎回来，${nextSession.user.username}。`
          : `工作区已创建，${nextSession.user.username}。`,
        tone: 'success',
      };
    } catch (error) {
      authNotice = {
        text: error instanceof Error ? error.message : '认证请求失败，请稍后重试。',
        tone: 'error',
      };
    } finally {
      isAuthSubmitting = false;
    }
  }

  function openDesktopClient(): void {
    saveBackendUrl(backendUrl);
    launchNotice = {
      text: '正在尝试打开桌面客户端。如果没有响应，请先安装客户端。',
      tone: 'info',
    };
    window.location.href = launchUrl;
  }

  async function copyLaunchUrl(): Promise<void> {
    try {
      await navigator.clipboard.writeText(launchUrl);
      launchNotice = {
        text: '已复制桌面客户端启动链接。',
        tone: 'success',
      };
    } catch {
      launchNotice = {
        text: '复制失败，请手动复制下方启动链接。',
        tone: 'error',
      };
    }
  }

  function guideArtifactUpload(): void {
    launchNotice = {
      text: 'Jar 上传、应用保存和启动在桌面客户端中完成。正在尝试打开客户端并同步当前会话。',
      tone: 'info',
    };
    openDesktopClient();
  }
</script>

<svelte:head>
  <title>Java 应用保护工作区</title>
  <meta
    name="description"
    content="Java 应用保护入口、账户认证和桌面客户端启动流程。"
  />
</svelte:head>

<div class="relative min-h-screen overflow-x-hidden">
  <a class="skip-link" href="#main-content">跳到主要内容</a>
  <div class="absolute inset-x-0 top-0 h-40 bg-[linear-gradient(180deg,rgba(19,102,214,0.08),transparent)]"></div>

  <main class="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-8 lg:px-8" id="main-content">
    {#if currentView === 'home'}
    <section class="panel overflow-hidden px-6 py-7 md:px-8 md:py-10" aria-labelledby="hero-title">
        <div class="max-w-4xl">
          <p class="eyebrow mb-3">Java Protection Platform</p>
          <h1 class="hero-title mb-5" id="hero-title">面向 Java 应用交付的保护与授权平台。</h1>
          <p class="muted-copy max-w-3xl text-base md:text-lg">
            用于发布前的应用保护、授权校验和受控分发。
          </p>
        </div>

        <div class="mt-8 flex flex-wrap gap-3">
          <button class="primary-btn inline-flex items-center gap-2 px-5 py-3 font-semibold" type="button" onclick={() => showAuth('login')}>
            登录 / 注册
            <KeyRound aria-hidden="true" size={17} />
          </button>
          <a class="secondary-btn inline-flex items-center gap-2 px-5 py-3 font-semibold no-underline" href="#features">
            了解能力
            <ArrowRight aria-hidden="true" size={17} />
          </a>
        </div>

    </section>

    <section class="grid gap-5" id="features" aria-labelledby="features-title">
      <div class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p class="eyebrow mb-3">Capability Surface</p>
          <h2 class="section-title m-0" id="features-title">核心能力</h2>
        </div>
      </div>

      <div class="grid gap-4 md:grid-cols-3">
        {#each featureItems as feature (feature.title)}
          <article class="panel overflow-hidden px-5 py-5 md:px-6 md:py-6">
            <div class="mb-5 flex items-start justify-between gap-4">
              <div class="min-w-0">
                <p class="eyebrow mb-2">{feature.kicker}</p>
                <h3 class="preview-title m-0 text-2xl font-semibold">{feature.title}</h3>
              </div>
              <div class="flex h-11 w-11 shrink-0 items-center justify-center border border-[rgba(19,102,214,0.18)] bg-[rgba(19,102,214,0.08)] text-[var(--web-accent)]">
                <feature.icon aria-hidden="true" size={18} />
              </div>
            </div>
            <p class="muted-copy m-0 text-sm">{feature.summary}</p>
          </article>
        {/each}
      </div>
    </section>

    <section class="grid gap-6" id="workflow" aria-labelledby="workflow-title">
      <article class="panel px-6 py-6">
        <p class="eyebrow mb-3">How It Works</p>
        <h2 class="section-title m-0" id="workflow-title">使用方式</h2>
        <div class="mt-6 grid gap-3">
          {#each workflowItems as step, index (step.title)}
            <div class="panel-soft grid gap-2 px-4 py-4">
              <p class="eyebrow m-0">Step {index + 1}</p>
              <h3 class="m-0 text-lg font-semibold">{step.title}</h3>
              <p class="muted-copy m-0 text-sm">{step.detail}</p>
            </div>
          {/each}
        </div>
      </article>
    </section>

    {:else if currentView === 'auth'}
      <section class="mx-auto grid w-full max-w-3xl gap-6" aria-labelledby="auth-title">
        <div class="panel px-6 py-6 md:px-8 md:py-8">
          <button class="ghost-btn mb-6 inline-flex items-center gap-2 px-4 py-3 font-semibold" type="button" onclick={showHome}>
            返回首页
          </button>

        <div class="mb-6 flex items-start justify-between gap-4">
          <div class="min-w-0">
            <p class="eyebrow mb-2">Account Entry</p>
            <h2 class="section-title m-0" id="auth-title">{authHeading}</h2>
            <p class="muted-copy mt-3 mb-0 max-w-xl">{authSubcopy}</p>
          </div>
          <div class="flex h-11 w-11 shrink-0 items-center justify-center border border-[rgba(19,102,214,0.18)] bg-[rgba(19,102,214,0.08)] text-[var(--web-accent)]">
            <KeyRound aria-hidden="true" size={18} />
          </div>
        </div>

        <div class="auth-toggle mb-6" role="group" aria-label="认证模式">
          <button
            class:auth-toggle__button--active={authMode === 'login'}
            class="auth-toggle__button"
            type="button"
            onclick={() => (authMode = 'login')}
          >
            登录
          </button>
          <button
            class:auth-toggle__button--active={authMode === 'register'}
            class="auth-toggle__button"
            type="button"
            onclick={() => (authMode = 'register')}
          >
            注册
          </button>
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          {#if authMode === 'register'}
            <label class="grid gap-2">
              <span class="field-label">用户名</span>
              <input
                bind:value={username}
                autocomplete="username"
                class="field-input"
                name="username"
                placeholder="例如 jvmsense-user…"
                spellcheck="false"
                type="text"
              />
            </label>
            <label class="grid gap-2">
              <span class="field-label">邮箱</span>
              <input
                bind:value={email}
                autocomplete="email"
                class="field-input"
                inputmode="email"
                name="email"
                placeholder="例如 user@example.com…"
                spellcheck="false"
                type="email"
              />
            </label>
          {:else}
            <label class="grid gap-2 md:col-span-2">
              <span class="field-label">用户名或邮箱</span>
              <input
                bind:value={identifier}
                autocomplete="username"
                class="field-input"
                name="identifier"
                placeholder="例如 user@example.com…"
                spellcheck="false"
                type="text"
              />
            </label>
          {/if}

          <label class="grid gap-2 md:col-span-2">
            <span class="field-label">密码</span>
            <input
              bind:value={password}
              autocomplete={authMode === 'login' ? 'current-password' : 'new-password'}
              class="field-input"
              name="password"
              placeholder="输入密码…"
              type="password"
            />
          </label>
        </div>

        <div class="mt-6 flex flex-wrap gap-3">
          <button
            class="primary-btn inline-flex items-center gap-2 px-5 py-3 font-semibold"
            disabled={isAuthSubmitting || !canSubmitAuth}
            type="button"
            onclick={() => void submitAuth()}
          >
            {primaryAuthLabel}
            <ArrowRight aria-hidden="true" size={16} />
          </button>

        </div>

        {#if authNotice}
          <div class="panel-soft status-card mt-5 px-4 py-4" data-tone={authNotice.tone} aria-live="polite">
            <p class="m-0 text-sm">{authNotice.text}</p>
          </div>
        {/if}

          <div class="panel-soft mt-6 flex items-start gap-3 px-4 py-4">
            <Sparkles aria-hidden="true" class="mt-0.5 shrink-0 text-[var(--web-accent)]" size={18} />
            <p class="m-0 text-sm text-[var(--web-text)]">
              登录注册独立于首页。项目、制品和客户端操作会在登录后的工作区出现。
            </p>
          </div>
        </div>
      </section>

    {:else}
      <section class="grid gap-6" aria-labelledby="workspace-title">
        <div class="panel flex flex-wrap items-center justify-between gap-4 px-6 py-6 md:px-8">
          <div class="min-w-0">
            <h1 class="section-title m-0" id="workspace-title">工作区</h1>
            {#if session}
              <p class="muted-copy mt-2 mb-0 break-words text-sm">
                {session.user.username} · {session.user.email} · {accountSummary}
              </p>
            {/if}
          </div>
          <button class="ghost-btn inline-flex items-center gap-2 px-4 py-3 font-semibold" type="button" onclick={clearSession}>
            退出登录
            <LogOut aria-hidden="true" size={16} />
          </button>
        </div>

        <div class="workspace-grid workspace-grid--single">
          <article class="panel px-6 py-6 md:px-8">
            <div class="card-heading">
              <div>
                <p class="eyebrow mb-2">Session</p>
                <h2 class="m-0 text-2xl font-semibold">会话状态</h2>
              </div>
              <Clock3 aria-hidden="true" class="text-[var(--web-accent)]" size={22} />
            </div>

            {#if session}
              <dl class="info-list mt-5">
                <div>
                  <dt>账户</dt>
                  <dd>{session.user.username}</dd>
                </div>
                <div>
                  <dt>邮箱</dt>
                  <dd>{session.user.email}</dd>
                </div>
                <div>
                  <dt>过期时间</dt>
                  <dd>{accountSummary}</dd>
                </div>
                <div>
                  <dt>剩余时间</dt>
                  <dd>{sessionRemainingLabel}</dd>
                </div>
              </dl>
            {/if}
          </article>
        </div>

        <div class="panel px-6 py-6 md:px-8">
          <div class="card-heading mb-5">
            <div>
              <p class="eyebrow mb-2">Desktop Bridge</p>
              <h2 class="m-0 text-2xl font-semibold">桌面客户端</h2>
            </div>
            <AppWindowMac aria-hidden="true" class="text-[var(--web-accent)]" size={22} />
          </div>

          <div class="grid gap-3 md:grid-cols-3">
            <button class="primary-btn inline-flex items-center justify-center gap-2 px-4 py-4 font-semibold" type="button" onclick={openDesktopClient}>
              打开客户端
              <ExternalLink aria-hidden="true" size={16} />
            </button>
            <button class="secondary-btn inline-flex items-center justify-center gap-2 px-4 py-4 font-semibold" type="button" onclick={() => void copyLaunchUrl()}>
              复制启动链接
              <Copy aria-hidden="true" size={16} />
            </button>
            <button class="secondary-btn inline-flex items-center justify-center gap-2 px-4 py-4 font-semibold" type="button" onclick={guideArtifactUpload}>
              上传制品
              <UploadCloud aria-hidden="true" size={16} />
            </button>
          </div>

          <div class="launch-url-box mt-5" aria-label="桌面客户端启动链接">
            <span>{launchUrl}</span>
          </div>

          <div class="panel-soft mt-5 flex items-start gap-3 px-4 py-4">
            <Boxes aria-hidden="true" class="mt-0.5 shrink-0 text-[var(--web-accent)]" size={18} />
            <p class="m-0 text-sm text-[var(--web-text)]">
              上传 Jar、创建应用和启动运行由桌面客户端完成。Web 入口负责同步当前后端和登录会话。
            </p>
          </div>
        </div>

        {#if launchNotice}
          <div class="panel-soft status-card px-4 py-4" data-tone={launchNotice.tone} aria-live="polite">
            <p class="m-0 text-sm text-[var(--web-text)]">{launchNotice.text}</p>
          </div>
        {/if}
      </section>
    {/if}
  </main>
</div>
